import React from 'react';

interface WindDirectionIconProps {
  /** Wind direction in degrees (0-360) or compass string (N, NE, E, etc.) */
  direction: string | number | null | undefined;
  /** Optional wind speed for color coding */
  windSpeed?: number | null;
  /** Size of the icon in pixels */
  size?: number;
  /** Whether to show the angle value in parentheses */
  showAngle?: boolean;
  /** Custom color for the arrow (overrides wind speed color) */
  color?: string;
  /** Additional CSS classes */
  className?: string;
  /** Additional inline styles */
  style?: React.CSSProperties;
}

// Wind speed color scale (matches ForecastChartView and StationCharts)
const windSpeedColors = [
  { max: 5, color: '#00CED1', label: '< 5 km/h' },      // Cyan
  { max: 10, color: '#32CD32', label: '5-10 km/h' },    // Green
  { max: 15, color: '#FFD700', label: '10-15 km/h' },   // Yellow
  { max: 20, color: '#FFA500', label: '15-20 km/h' },   // Orange
  { max: Infinity, color: '#FF4444', label: '> 20 km/h' }, // Red
];

const getWindSpeedColor = (speed: number | null | undefined): string => {
  if (speed === null || speed === undefined) return '#666';
  for (const { max, color } of windSpeedColors) {
    if (speed < max) return color;
  }
  return '#FF4444';
};

/**
 * Convert wind direction to degrees
 */
const parseWindDirection = (direction: string | number | null | undefined): number | null => {
  if (!direction && direction !== 0) return null;
  
  let degrees: number;
  
  // If it's a string, try to convert compass direction to degrees
  if (typeof direction === 'string') {
    const compassMap: Record<string, number> = {
      'N': 0,
      'NNE': 22.5,
      'NE': 45,
      'ENE': 67.5,
      'E': 90,
      'ESE': 112.5,
      'SE': 135,
      'SSE': 157.5,
      'S': 180,
      'SSW': 202.5,
      'SW': 225,
      'WSW': 247.5,
      'W': 270,
      'WNW': 292.5,
      'NW': 315,
      'NNW': 337.5,
    };
    
    const upper = direction.toUpperCase().trim();
    if (compassMap[upper] !== undefined) {
      degrees = compassMap[upper];
    } else {
      // Try to parse as number
      const parsed = parseFloat(direction);
      if (isNaN(parsed)) return null;
      degrees = parsed;
    }
  } else {
    degrees = direction;
  }
  
  // Normalize degrees to 0-360
  degrees = degrees % 360;
  if (degrees < 0) degrees += 360;
  
  return degrees;
};

/**
 * WindDirectionIcon component that renders an SVG arrow rotated to the exact wind direction angle
 * and optionally displays the angle value in parentheses.
 */
export const WindDirectionIcon: React.FC<WindDirectionIconProps> = ({
  direction,
  windSpeed,
  size = 24,
  showAngle = true,
  color,
  className,
  style,
}) => {
  const degrees = parseWindDirection(direction);
  
  if (degrees === null) {
    return null;
  }
  
  // Wind direction is where the wind is coming FROM, so we add 180° to show where it's going
  // SVG rotation: 0° = pointing right, so we adjust: North (0°) should point up
  const rotation = (degrees + 180) % 360;
  
  // Determine arrow color
  const arrowColor = color || getWindSpeedColor(windSpeed);
  
  // Calculate arrow size based on prop
  const arrowSize = size;
  const arrowHeadSize = arrowSize * 0.375; // Proportional to ForecastChartView
  const arrowShaftLength = arrowSize * 0.5;
  
  return (
    <span
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        ...style,
      }}
    >
      <svg
        width={arrowSize}
        height={arrowSize}
        viewBox={`-${arrowSize / 2} -${arrowSize / 2} ${arrowSize} ${arrowSize}`}
        style={{ display: 'inline-block', verticalAlign: 'middle' }}
      >
        <g transform={`rotate(${rotation})`}>
          <path
            d={`M0,-${arrowShaftLength} L${arrowHeadSize},${arrowShaftLength * 0.67} L0,${arrowShaftLength * 0.33} L-${arrowHeadSize},${arrowShaftLength * 0.67} Z`}
            fill={arrowColor}
            stroke="#333"
            strokeWidth={0.5}
          />
        </g>
      </svg>
      {showAngle && (
        <span style={{ fontSize: size * 0.6, lineHeight: 1 }}>
          ({Math.round(degrees)}°)
        </span>
      )}
    </span>
  );
};
